/**
 * Umami Analytics utility for GDPR-compliant event tracking.
 *
 * Wraps the global umami instance (loaded via script tag in production)
 * with data sanitisation to prevent sensitive information from being tracked.
 *
 * Self-hosted at analytics.gifty.nl — user/company identification is safe
 * as data never leaves our infrastructure.
 */

const sensitiveKeys = [
    'password',
    'password_confirmation',
    'card_number',
    'card_cvc',
    'card_expiry',
    'giftcard',
    'gift_card',
    'giftCard',
    'code',
    'token',
    'api_token',
    'secret',
    'email',
    'phone',
    'dob',
    'date_of_birth',
    'iban',
    'bsn',
    'ip',
    'ip_address',
]

/**
 * Remove sensitive keys from an object before sending to analytics.
 */
function sanitise(data) {
    if (data === null || data === undefined || typeof data !== 'object') {
        return data
    }

    const cleaned = {}

    for (const key of Object.keys(data)) {
        if (sensitiveKeys.includes(key.toLowerCase()) === false) {
            cleaned[key] = data[key]
        }
    }

    return cleaned
}

/**
 * Check whether the Umami script is loaded and available.
 */
function isAvailable() {
    return typeof window !== 'undefined' && typeof window.umami !== 'undefined'
}

const umami = {
    /**
     * Track a custom event with optional data.
     * Sensitive fields are automatically stripped.
     */
    track(eventName, data) {
        if (isAvailable() === false) {
            return
        }

        if (data) {
            window.umami.track(eventName, sanitise(data))
        } else {
            window.umami.track(eventName)
        }
    },

    /**
     * Identify the current user and their active company.
     * Safe for self-hosted Umami — data stays on analytics.gifty.nl.
     */
    identify(user, company) {
        if (isAvailable() === false) {
            return
        }

        const payload = {}

        if (user) {
            payload.user_id = user.id
        }

        if (company) {
            payload.company_id = company.id
            payload.company_name = company.name
        }

        window.umami.identify(payload)
    },

    /**
     * Reset identification (on logout).
     */
    reset() {
        if (isAvailable() === false) {
            return
        }

        window.umami.identify({
            user_id: null,
            company_id: null,
            company_name: null,
        })
    },
}

export default umami
